import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiShoppingCart, FiCheck, FiX, FiGlobe, FiSearch } from 'react-icons/fi';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import Navigation from '../components/Navigation';

export default function Products() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [domainSearch, setDomainSearch] = useState('');
  const [domainResults, setDomainResults] = useState(null);
  const [searchingDomain, setSearchingDomain] = useState(false);
  const [domainYears, setDomainYears] = useState({}); // Track years for each domain

  useEffect(() => {
    fetchProducts();
    
    // Check for domain search from URL query parameter
    const domainParam = searchParams.get('domain');
    if (domainParam) {
      setDomainSearch(domainParam);
      // Trigger search automatically
      searchDomainAuto(domainParam);
    }
  }, [searchParams]);

  const fetchProducts = async () => {
    try {
      const response = await api.getProducts();
      // Handle new API response format (products + source)
      const productList = response.data?.products || response.data || [];
      setProducts(productList);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    // Redirect to product's HostShop order URL if available
    if (product.orderUrl) {
      window.location.href = product.orderUrl;
    } else {
      // Fallback to generic hostshop if no order URL is set
      alert('Product does not have an order URL configured yet.');
    }
  };

  const handleSearchDomain = async () => {
    if (!domainSearch.trim()) return;

    setSearchingDomain(true);
    try {
      const response = await api.searchDomains(domainSearch);
      setDomainResults(response.data);
    } catch (error) {
      console.error('Failed to search domain:', error);
      alert('Failed to search domain. Please try again.');
    } finally {
      setSearchingDomain(false);
    }
  };

  const searchDomainAuto = async (domain) => {
    if (!domain.trim()) return;

    setSearchingDomain(true);
    try {
      const response = await api.searchDomains(domain);
      setDomainResults(response.data);
    } catch (error) {
      console.error('Failed to search domain:', error);
    } finally {
      setSearchingDomain(false);
    }
  };

  const handleAddDomainToCart = (domainResult) => {
    const years = domainYears[domainResult.domain] || 1;
    // Redirect to domain search/order page with domain and years
    const domainURL = `https://account.maestrohosts.com/domain-search?domain=${encodeURIComponent(domainResult.domain)}&years=${years}`;
    window.location.href = domainURL;
  };

  const filteredProducts = filterType === 'all' 
    ? products 
    : products.filter(p => p.type === filterType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Navigation />

      {/* Domain Search Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-4 text-center">Find Your Perfect Domain</h2>
          <p className="text-blue-100 text-center mb-8">Search millions of domains and register yours today</p>
          
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-4 top-3.5 text-gray-400" size={20} />
              <input
                type="text"
                value={domainSearch}
                onChange={(e) => setDomainSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchDomain()}
                placeholder="search your domain name..."
                className="w-full pl-12 pr-4 py-3 rounded-lg border-2 border-white focus:outline-none text-lg"
              />
            </div>
            <button
              onClick={handleSearchDomain}
              disabled={searchingDomain}
              className="bg-white hover:bg-blue-50 text-blue-600 px-8 py-3 rounded-lg font-bold transition-all disabled:opacity-50"
            >
              {searchingDomain ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Domain Search Results */}
          {domainResults && (
            <div className="mt-8 bg-white rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Domain Results for <span className="text-blue-600">{domainResults.searchTerm}</span>
              </h3>
              {domainResults.summary && (
                <p className="text-sm text-gray-600 mb-4">
                  {domainResults.summary.available} available of {domainResults.summary.total} options
                </p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {domainResults.results && domainResults.results.map((result, idx) => (
                  <div key={idx} className="border-2 border-gray-200 rounded-lg p-4 hover:border-green-500 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900 text-sm">{result.domain}</span>
                      {result.available ? (
                        <FiCheck className="text-green-600" />
                      ) : (
                        <FiX className="text-red-600" />
                      )}
                    </div>
                    {result.available && (
                      <>
                        <p className="text-blue-600 font-bold mb-2">
                          ${(result.price * (domainYears[result.domain] || 1)).toFixed(2)}
                          <span className="text-sm text-gray-600">/year</span>
                        </p>
                        
                        <div className="mb-3">
                          <label className="text-xs text-gray-700 block mb-1">Years:</label>
                          <select
                            value={domainYears[result.domain] || 1}
                            onChange={(e) =>
                              setDomainYears((prev) => ({
                                ...prev,
                                [result.domain]: parseInt(e.target.value),
                              }))
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                              <option key={year} value={year}>
                                {year} year{year > 1 ? 's' : ''} - ${(result.price * year).toFixed(2)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          onClick={() => handleAddDomainToCart(result)}
                          className="w-full bg-blue-600 text-white py-2 rounded font-semibold hover:bg-blue-700 text-sm"
                        >
                          Add to Cart
                        </button>
                      </>
                    )}
                    {!result.available && (
                      <p className="text-gray-500 text-sm">Taken</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Products Section */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-6">Our Hosting Plans</h2>
          
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {['all', 'shared', 'vps', 'dedicated', 'ssl', 'email'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-6 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                  filterType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-white mt-4">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600 text-lg">No products available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:scale-105 cursor-pointer ${
                  product.isPopular ? 'ring-2 ring-blue-600 lg:scale-105' : 'bg-white'
                }`}
                onClick={() => setSelectedProduct(product)}
              >
                {product.isPopular && (
                  <div className="bg-blue-600 text-white px-4 py-2 text-center font-bold text-sm">
                    ⭐ Most Popular
                  </div>
                )}
                
                <div className={product.isPopular ? 'bg-blue-50 p-6' : 'bg-white p-6'}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-blue-600">
                      ${parseFloat(product.monthlyPrice).toFixed(2)}
                    </span>
                    <span className="text-gray-600"> /month</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {product.diskStorage && (
                      <li className="flex items-center gap-2 text-gray-700">
                        <FiCheck size={18} className="text-green-600" />
                        {product.diskStorage}GB Storage
                      </li>
                    )}
                    {product.bandwidth && (
                      <li className="flex items-center gap-2 text-gray-700">
                        <FiCheck size={18} className="text-green-600" />
                        {product.bandwidth}GB Bandwidth
                      </li>
                    )}
                    {product.features && product.features.length > 0 && (
                      product.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-700">
                          <FiCheck size={18} className="text-green-600" />
                          {feature}
                        </li>
                      ))
                    )}
                  </ul>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(product);
                    }}
                    className={`w-full py-3 rounded-lg font-bold transition-all ${
                      product.isPopular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Product Details Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-8">
              <button
                onClick={() => setSelectedProduct(null)}
                className="float-right text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>

              <h2 className="text-3xl font-bold text-gray-900 mb-3">{selectedProduct.name}</h2>
              <p className="text-gray-600 mb-6">{selectedProduct.description}</p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-blue-600">
                  ${parseFloat(selectedProduct.monthlyPrice).toFixed(2)}
                </span>
                <span className="text-gray-600 ml-2">/month</span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8 bg-gray-50 p-4 rounded-lg">
                {selectedProduct.diskStorage && (
                  <div>
                    <p className="text-gray-600 text-sm">Storage</p>
                    <p className="text-2xl font-bold text-gray-900">{selectedProduct.diskStorage}GB</p>
                  </div>
                )}
                {selectedProduct.bandwidth && (
                  <div>
                    <p className="text-gray-600 text-sm">Bandwidth</p>
                    <p className="text-2xl font-bold text-gray-900">{selectedProduct.bandwidth}GB</p>
                  </div>
                )}
              </div>

              {selectedProduct.features && selectedProduct.features.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Features</h3>
                  <ul className="space-y-2">
                    {selectedProduct.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-700">
                        <FiCheck size={20} className="text-green-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => {
                  handleAddToCart(selectedProduct);
                  setSelectedProduct(null);
                }}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-all"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
